import styled from '@emotion/styled';
import { Select } from 'antd';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import Flag from 'react-world-flags';

import { Dispatch } from 'src/config/rootStore';
import { getSortedCountries } from 'src/modules/countries/selectors/countrySelectors';
import { getSortedLanguages } from 'src/modules/languages/selectors/languageSelectors';
import { BrandWrapper } from 'src/modules/onboarding/components/BrandWrapper';
import { getUserInformation } from 'src/modules/user/selectors/userSelectors';
import { Button } from 'src/shared/components/Button';
import { Input } from 'src/shared/components/Input';
import { availabilityOptions } from 'src/shared/models/shared';
import { color, font, shadow } from 'src/shared/styles/variables';
import {
    usaStatesOptions,
    canadaProvincesOptions,
} from 'src/modules/insuranceIntegration/models/statesOptions';
import { AdditionalInfoTestID } from './testIds';

const Wrapper = styled.div({
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
    flex: 1,
    maxHeight: 1000,
});

const Title = styled.h2(
    {
        color: color.pickledBluewood,
        marginBottom: 16,
        textAlign: 'center',
    },
    font.black(font.size.headerExtended),
);

const Subtitle = styled.h2(
    {
        color: color.steelGray,
    },
    font.roman(font.size.mediumExtended),
);

const ContentWrapper = styled.div({
    marginTop: 10,
    maxWidth: 490,
    width: '100%',
});

const StyledSelect = styled(Select)({
    width: '100%',
    '.ant-select-selection--single, .ant-select-selection--multiple': {
        minHeight: 50,
        boxShadow: shadow.regularShadow,
        borderRadius: 5,
        border: `1px solid ${color.linkWaterLight}`,
    },
    '.ant-select-selection--multiple': {
        cursor: 'pointer',
    },
    '.ant-select-selection__rendered': {
        lineHeight: '48px',
        display: 'flex',
        alignItems: 'center',
    },
    '.ant-select-selection__placeholder': {
        color: color.fiord,
        ...font.heavy(font.size.base),
    },
    '.ant-select-selection--multiple .ant-select-arrow': {
        top: '24px',
    },
});

const OptionWrapper = styled.div({
    display: 'flex',
    alignItems: 'center',
});

const OptionText = styled.span(
    {
        color: color.fiord,
    },
    font.heavy(font.size.base),
);

const CountryName = styled(OptionText)({
    marginBottom: -3,
    marginLeft: 14,
});

const StateName = styled(OptionText)({
    marginBottom: -3,
});

const FieldLabel = styled.p(
    {
        color: color.steelGray,
        margin: '20px 0 7px !important',
    },
    font.heavy(font.size.base),
);

const AsteriskLabel = styled.span({
    color: color.wildWatermelon,
});

const FieldsWrapper = styled.div({
    display: 'flex',
    justifyContent: 'space-between',
});

const FieldWrapper = styled.div({
    marginBottom: 5,
    flex: 0.48,
});

const ButtonsWrapper = styled.div({
    marginTop: 35,
    display: 'flex',
    justifyContent: 'space-between',
});

interface Props {
    onSubmit?: () => void;
    onPreviousPress?: () => void;
    useBrandWrapper?: boolean;
}

export const AdditionalInfo: React.FC<Props> = ({ onSubmit, useBrandWrapper, onPreviousPress }) => {
    const { t } = useTranslation();
    const user = useSelector(getUserInformation);
    const countries = useSelector(getSortedCountries);
    const languagesOptions = useSelector(getSortedLanguages);

    const [loading, setLoading] = useState(false);
    const dispatch = useDispatch<Dispatch>();

    const [country, setCountry] = useState(user.country || 'United States');
    const [zipCode, setZipCode] = useState(user.zipCode || '');
    const [state, setState] = useState(user.state || '');
    const [city, setCity] = useState(user.city || '');
    const [phoneNumber, setPhoneNumber] = useState(user.phoneNumber || '');
    const [languages, setLanguages] = useState(
        user.languagePreferences.length >= 1 ? user.languagePreferences : ['English'],
    );
    const [availability, setAvailability] = useState<string[]>(user.availability || []);

    const isUSSelected = country === 'United States';
    const isCanadaSelected = country === 'Canada';

    const isFormValid = () => {
        if (!country || !city || !availability?.length || !languages?.length) {
            return false;
        }

        if (isCanadaSelected && !state) {
            return false;
        }

        return isUSSelected ? zipCode && state : true;
    };

    const handleZipCodeChange = (value: string) => {
        // Postal codes are complicated internationally.
        // For now, validate only the first half of
        // US postal codes and accept anything for other countries.
        if (country === 'United States' && value.trim().length > 6) {
            return;
        }

        setZipCode(value);
    };

    const handleCountryChange = (newCountry: string) => {
        if (country === newCountry) {
            return;
        }
        // clear state, city and zip code since the country has changed
        setState('');
        setZipCode('');
        setCity('');
        setCountry(newCountry);
    };

    const handleSubmit = async () => {
        if (!isFormValid() && loading) {
            return;
        }

        setLoading(true);

        await dispatch.user.updateUserInfo({
            languagePreferences: languages,
            country,
            availability,
            zipCode,
            city,
            state,
            phoneNumber: phoneNumber || user.phoneNumber,
        });

        setLoading(false);

        if (onSubmit) {
            onSubmit();
        }
    };

    const WrapperComponent = useBrandWrapper ? BrandWrapper : React.Fragment;

    const handleLanguageSelection = (language: string) => setLanguages([language]);

    const formValid = isFormValid();

    const statesOptions = isUSSelected
        ? usaStatesOptions
        : isCanadaSelected
        ? canadaProvincesOptions
        : null;

    return (
        <WrapperComponent>
            <Wrapper>
                <Title>{t('providers:additional_info.title')}</Title>
                <Subtitle>{t('providers:additional_info.subtitle')}</Subtitle>

                <ContentWrapper>
                    <FieldLabel>
                        {t('providers:additional_info.country_region')}{' '}
                        <AsteriskLabel>*</AsteriskLabel>
                    </FieldLabel>
                    <StyledSelect
                        data-testid={AdditionalInfoTestID.CountryPicker}
                        onChange={handleCountryChange}
                        value={country}
                    >
                        {countries.map(({ isoCode, text, value }, i) => (
                            <Select.Option
                                key={value}
                                value={value}
                                data-testid={`${AdditionalInfoTestID.CountryPicker}-option-${i}`}
                            >
                                <OptionWrapper>
                                    <Flag code={isoCode} width="36" height="24" />
                                    <CountryName>{text}</CountryName>
                                </OptionWrapper>
                            </Select.Option>
                        ))}
                    </StyledSelect>
                    {statesOptions ? (
                        <>
                            <FieldLabel>
                                {isUSSelected
                                    ? t('providers:additional_info.state')
                                    : t('providers:additional_info.province_or_territory')}{' '}
                                <AsteriskLabel>*</AsteriskLabel>
                            </FieldLabel>
                            <StyledSelect
                                data-testid={AdditionalInfoTestID.StatePicker}
                                onChange={(value: string) => setState(value)}
                                value={state}
                            >
                                {statesOptions.map((stateOption, i) => (
                                    <Select.Option
                                        key={stateOption.fullName}
                                        value={stateOption.fullName}
                                        data-testid={`${AdditionalInfoTestID.StatePicker}-option-${i}`}
                                    >
                                        <OptionWrapper>
                                            <StateName>{stateOption.fullName}</StateName>
                                        </OptionWrapper>
                                    </Select.Option>
                                ))}
                            </StyledSelect>
                        </>
                    ) : null}
                    <FieldsWrapper>
                        <FieldWrapper>
                            <Input
                                fieldId="city"
                                testID={AdditionalInfoTestID.CityInput}
                                label={t('providers:additional_info.city')}
                                type="text"
                                value={city}
                                onChange={setCity}
                                showAsterisk
                            />
                        </FieldWrapper>

                        <FieldWrapper>
                            <Input
                                fieldId="zipCode"
                                testID={AdditionalInfoTestID.ZipCodeInput}
                                label={t('providers:additional_info.postal_code')}
                                type="text"
                                value={zipCode}
                                onChange={handleZipCodeChange}
                                showAsterisk={isUSSelected}
                            />
                        </FieldWrapper>
                    </FieldsWrapper>

                    <FieldsWrapper>
                        <FieldWrapper>
                            <FieldLabel>
                                {t('providers:additional_info.language_preferences')}{' '}
                                <AsteriskLabel>*</AsteriskLabel>
                            </FieldLabel>
                            <StyledSelect
                                data-testid={AdditionalInfoTestID.LanguagePicker}
                                onChange={handleLanguageSelection}
                                value={languages}
                            >
                                {languagesOptions.map(({ text, value }, i) => (
                                    <Select.Option
                                        key={value}
                                        value={value}
                                        data-testid={`${AdditionalInfoTestID.LanguagePicker}-option-${i}`}
                                    >
                                        <OptionWrapper>
                                            <OptionText>{text}</OptionText>
                                        </OptionWrapper>
                                    </Select.Option>
                                ))}
                            </StyledSelect>
                        </FieldWrapper>

                        <FieldWrapper>
                            <FieldLabel>
                                {t('providers:additional_info.availability')}{' '}
                                <AsteriskLabel>*</AsteriskLabel>
                            </FieldLabel>
                            <StyledSelect
                                data-testid={AdditionalInfoTestID.AvailabilityPicker}
                                onChange={(value: string[]) => setAvailability(value)}
                                mode="multiple"
                                placeholder={t('providers:additional_info.select_all_that_match')}
                                value={availability}
                                showArrow
                            >
                                {availabilityOptions.map((option, i) => (
                                    <Select.Option
                                        key={option}
                                        value={option}
                                        data-testid={`${AdditionalInfoTestID.AvailabilityPicker}-option-${i}`}
                                    >
                                        <OptionWrapper>
                                            <OptionText>
                                                {t(
                                                    `providers:additional_info.availabilityTime.${option.toLocaleLowerCase()}`,
                                                )}
                                            </OptionText>
                                        </OptionWrapper>
                                    </Select.Option>
                                ))}
                            </StyledSelect>
                        </FieldWrapper>
                    </FieldsWrapper>
                    <Input
                        fieldId="phoneNumber"
                        label={t('providers:additional_info.phone_number')}
                        type="text"
                        value={phoneNumber}
                        onChange={setPhoneNumber}
                    />
                    <ButtonsWrapper>
                        {onPreviousPress ? (
                            <Button
                                testID={AdditionalInfoTestID.PreviousButton}
                                onClick={onPreviousPress}
                                widthPercentage={48}
                                type="cancel"
                            >
                                {t('common.previous')}
                            </Button>
                        ) : null}
                        <Button
                            onClick={handleSubmit}
                            testID={AdditionalInfoTestID.SubmitButton}
                            disabled={!formValid}
                            withShadow={!!formValid}
                            loading={loading}
                            widthPercentage={onPreviousPress ? 48 : 100}
                        >
                            {t('common.continue')}
                        </Button>
                    </ButtonsWrapper>
                </ContentWrapper>
            </Wrapper>
        </WrapperComponent>
    );
};
